import { AppZIndexes } from "@app/app/constants/AppZIndexes";
import { z } from "zod/v4";

export interface HostApiMethodData {
    methodName: string;
    functionSchema: z.core.$ZodFunction;
    description: string;
}

/**
 * Registry for host API methods
 * This is used to generate documentation for the host API
 */
export const hostApiRegistry = z.registry<{
    description: string;
    examples: Array<Record<string, any>>;
}>();

/**
 * Search params schema
 * This is used to validate the search params
 */
const searchParamsSchema = z
    .object({
        propertyId: z.string().optional().describe("The ID of the property to search for"),
        checkin: z.coerce.date().describe("Check-in date"),
        checkout: z.coerce.date().describe("Check-out date"),
        adultCount: z.number().int().min(1).optional().describe("Number of adults"),
        childCount: z.number().int().min(0).optional().describe("Number of children"),
        infantCount: z.number().int().min(0).optional().describe("Number of infants"),
        roomCount: z.number().int().min(1).optional().describe("Number of rooms"),
        promoCode: z.string().optional().describe("Promotional code"),
        languageCode: z.string().optional().describe("Language code for the search"),
    })
    .optional()
    .register(hostApiRegistry, {
        description: "Open the Hotelchamp IBE widget with the given parameters",
        examples: [
            {
                propertyId: "123",
                checkin: new Date("2024-03-01"),
                checkout: new Date("2024-03-05"),
                adultCount: 2,
                childCount: 1,
                infantCount: 0,
            },
        ],
    });

const openLoyaltyModalSchema = z
    .tuple([
        z.boolean().default(true).describe("State of the loyalty modal (true = open, false = close)"),
        z.boolean().default(true).describe("Open the IBe with the modal"),
    ])
    .register(hostApiRegistry, {
        description: "Open the loyalty modal",
        examples: [
            {
                input: [true, true],
            },
        ],
    });

export type SearchParams = z.infer<typeof searchParamsSchema>;

export const hostApiMethods = [
    {
        methodName: "search",
        functionSchema: z.function({
            input: z.tuple([searchParamsSchema]).or(z.tuple([])), // void is a fix for the validator as tuple with undefined is not allowed
            output: z.void(),
        }),
        description: "Search for a property",
    },
    {
        methodName: "getWidgetState",
        functionSchema: z.function({
            input: [z.void()],
            output: z.string(),
        }),
        description: "Get the widget state",
    },
    {
        methodName: "detectLanguage",
        functionSchema: z.function({
            input: [z.void()],
            output: z.void(),
        }),
        description: "Detect the language",
    },
    {
        methodName: "setLanguage",
        functionSchema: z.function({
            input: [z.string()],
            output: z.void(),
        }),
        description: "Set the language",
    },
    {
        methodName: "requestWidgetTransition",
        functionSchema: z.function({
            input: [z.string()],
            output: z.void(),
        }),
        description: "Request the widget transition",
    },
    {
        methodName: "getAppZIndexes",
        functionSchema: z.function({
            input: [z.void()],
            output: z.custom<typeof AppZIndexes>(),
        }),
        description: "Get the app z indexes",
    },
    {
        methodName: "openLoyaltyModal",
        functionSchema: z.function({
            input: openLoyaltyModalSchema,
            output: z.void(),
        }),
        description: "Open the loyalty modal",
    },
] as const satisfies HostApiMethodData[];

export const privateApiMethods = [
    {
        methodName: "getBooking",
        functionSchema: z.function({
            input: [z.void()],
            output: z.any(),
        }),
        description: "Get the booking",
    },
] as const satisfies HostApiMethodData[];

// ********** HOST API defs **********
export type HostApiMethodDefinitions = typeof hostApiMethods;
export type HostApiMethodName = HostApiMethodDefinitions[number]["methodName"];

// this is used to pre-register the methods in the window api namespace
// for tree shaking purposes this is an separate array from the hostApiMethods
// if keys are not found in this array, they will not be pre-registered in the window api namespace
// THIS HAS BEEN MOVED TO the main.ts file PLEASE UPATE THER IF ANY NEW METHODS ARE ADDED !!!!!!!!!!!!!!!!!!!!!!!!

export type HostApiMethodByOptionsName<T extends HostApiMethodName> = Extract<HostApiMethodDefinitions[number], { methodName: T }>;

export type HostFunctionSchema<T extends HostApiMethodName> = HostApiMethodByOptionsName<T>["functionSchema"];

export type HostApiMethodParameterType<T extends HostApiMethodName> = Parameters<HostFunctionSchema<T>["_input"]>;
export type HostApiMethodOutputType<T extends HostApiMethodName> = ReturnType<HostFunctionSchema<T>["_input"]>;
export type HostApiMethodCallBack<T extends HostApiMethodName> = (
    ...args: HostApiMethodParameterType<T>
) => HostApiMethodOutputType<T> | Promise<HostApiMethodOutputType<T>>;

// ********** PRIVATE API defs  **********

export type PrivateApiMethodDefinitions = typeof privateApiMethods;
export type PrivateApiMethodName = PrivateApiMethodDefinitions[number]["methodName"];

// this is used to pre-register the methods in the window api namespace
// for tree shaking purposes this is an separate array from the privateApiMethods
// if keys are not found in this array, they will not be pre-registered in the window api namespace
// THIS HAS BEEN MOVED TO the main.ts file PLEASE UPATE THER IF ANY NEW METHODS ARE ADDED !!!!!!!!!!!!!!!!!!!!!!!!

export type PrivateApiMethodByOptionsName<T extends PrivateApiMethodName> = Extract<PrivateApiMethodDefinitions[number], { methodName: T }>;

export type PrivateFunctionSchema<T extends PrivateApiMethodName> = PrivateApiMethodByOptionsName<T>["functionSchema"];

export type PrivateApiMethodParameterType<T extends PrivateApiMethodName> = Parameters<PrivateFunctionSchema<T>["_input"]>;
export type PrivateApiMethodOutputType<T extends PrivateApiMethodName> = ReturnType<PrivateFunctionSchema<T>["_input"]>;
export type PrivateApiMethodCallBack<T extends PrivateApiMethodName> = (
    ...args: PrivateApiMethodParameterType<T>
) => PrivateApiMethodOutputType<T> | Promise<PrivateApiMethodOutputType<T>>;
